// src/ClinicalDataCollector/components/output/AllergiesSummary.jsx
import React, { useContext, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  Alert,
} from '@mui/material';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useSelector } from 'react-redux';
import { AdvancedMdContext } from '../../../../context/AdvancedMD/AdvancedMdContext';

const AllergiesSummary = () => {
  // 🔹 Id de AdvancedMD desde Redux
  const { advancedMdPatientId } = useSelector(
    (state) => state.patientMapSelect
  );

  // 🔹 Contexto AdvancedMD (Allergies)
  const {
    allergies,
    allergiesLoading,
    allergiesError,
    loadAllergies,
  } = useContext(AdvancedMdContext);

  // 🔹 Cargar alergias cuando tengamos advancedMdPatientId
  useEffect(() => {
    if (!advancedMdPatientId) return;
    loadAllergies(advancedMdPatientId);
  }, [advancedMdPatientId, loadAllergies]);

  const hasAllergies = Array.isArray(allergies) && allergies.length > 0;
  const first = hasAllergies ? allergies[0] : null;

  const isNKDA =
    hasAllergies &&
    allergies.length === 1 &&
    (allergies[0].allergy || '').toUpperCase().includes('NKDA');

  return (
    <Paper elevation={1} sx={{ p: 2, mb: 3 }}>
      <Typography
        variant="h6"
        sx={{ display: 'flex', alignItems: 'center', mb: 1 }}
      >
        <ReportProblemIcon color="warning" />
        <Box ml={1}>Allergies (AdvancedMD)</Box>
      </Typography>

      {allergiesLoading && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Loading allergies from AdvancedMD…
        </Typography>
      )}

      {allergiesError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading allergies: {allergiesError.message || 'Unknown error'}
        </Alert>
      )}

      {!allergiesLoading && !allergiesError && !hasAllergies && (
        <Typography variant="body2" color="text.secondary">
          No allergy records found for this patient.
        </Typography>
      )}

      {/* Caso especial NKDA (No Known Drug Allergies) */}
      {isNKDA && (
        <Box sx={{ mt: 1 }}>
          <Chip
            icon={<WarningAmberIcon />}
            color="success"
            label="NKDA – No Known Drug Allergies"
          />
          <Typography variant="caption" display="block" sx={{ mt: 1 }}>
            Recorded by {first.userName || '—'} on{' '}
            {first.updateDate
              ? new Date(first.updateDate).toLocaleString()
              : '—'}
            .
          </Typography>
        </Box>
      )}

      {/* Lista general de alergias */}
      {!isNKDA && hasAllergies && (
        <>
          <Grid container spacing={1} sx={{ mb: 1 }}>
            {first && (
              <>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Last updated
                  </Typography>
                  <Typography variant="body2">
                    {first.updateDate
                      ? new Date(first.updateDate).toLocaleString()
                      : '—'}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary">
                    Recorded by
                  </Typography>
                  <Typography variant="body2">
                    {first.userName || '—'}
                  </Typography>
                </Grid>
              </>
            )}
          </Grid>

          <List dense>
            {allergies.map((a) => {
              const primary = a.allergy || 'Unknown allergy';
              const secondaryParts = [];

              if (a.reaction) secondaryParts.push(`Reaction: ${a.reaction}`);
              if (a.treatment) secondaryParts.push(`Treatment: ${a.treatment}`);
              if (a.status) secondaryParts.push(`Status: ${a.status}`);

              const secondary =
                secondaryParts.length > 0 ? secondaryParts.join(' | ') : null;

              return (
                <ListItem key={a.id}>
                  <ListItemText
                    primary={primary}
                    secondary={secondary}
                  />
                </ListItem>
              );
            })}
          </List>
        </>
      )}
    </Paper>
  );
};

export default AllergiesSummary;
